import { useAuth } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useEffect, useMemo } from "react";

import { Breadcrumb } from "~/components/ui/breadcrumb";
import { cn } from "~/lib/cn";
import {
  isDiscoveryStateDefault,
  replaceDiscoveryState,
  useDiscoveryState,
} from "~/lib/discovery/state";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { transformUserPreferencesToDiscoveryForm } from "~/lib/transforms/user-preferences";

// ---------------------------------------------------------------------------
// Server function: load user preferences for pre-population
// ---------------------------------------------------------------------------

const defaultValues = {
  goals: [],
  otherGoal: undefined,
  startTime: "",
  support: "",
  coachingFormats: "any",
  coachingApproaches: [],
  coachingPackages: [],
  coachGender: "",
  coachAges: [],
  userPersonality: [],
  coachPersonality: [],
  faithIntegration: [],
  preferredLanguages: ["English"],
  personalContext: "",
  sessionDeliveryMethods: "",
  prefersCoachLocation: "No preference",
  coachLocationRegion: undefined,
  coachLocationCountry: undefined,
  otherFaithIntegration: undefined,
};

const getRefinePreferences = createServerFn({ method: "GET" }).handler(
  async () => {
    const { getDiscoveryPreferences } =
      await import("~/lib/server/discovery-visitor");
    const prefs = await getDiscoveryPreferences();
    return prefs
      ? transformUserPreferencesToDiscoveryForm(prefs)
      : defaultValues;
  },
);

// ---------------------------------------------------------------------------
// Section config
// ---------------------------------------------------------------------------

const SECTIONS = [
  { label: "Coaching Style", pathSegment: "" },
  { label: "Personal Fit", pathSegment: "/fit" },
  { label: "Logistics", pathSegment: "/logistics" },
] as const;

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/refine")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["refinePreferences", ""],
      queryFn: () => getRefinePreferences(),
      staleTime: 1000 * 60 * 5,
    });
  },
  head: () =>
    createNoIndexMeta(
      "Refine Your Matches — Dream Coach Match",
      "Answer a few more questions to get better coach matches.",
    ),
  component: RefineLayout,
});

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function RefineLayout() {
  const location = useLocation();
  const auth = useAuth();
  const userId = auth.userId || "";

  const { data: userPreferences } = useQuery({
    queryKey: ["refinePreferences", userId],
    queryFn: () => getRefinePreferences(),
  });

  const { form: discoveryForm } = useDiscoveryState();
  const stateIsDefault = useMemo(
    () => isDiscoveryStateDefault(discoveryForm),
    [discoveryForm],
  );

  // Load DB preferences into localStorage on mount
  useEffect(() => {
    if (!userPreferences || (!userId && !stateIsDefault)) return;
    replaceDiscoveryState(
      transformUserPreferencesToDiscoveryForm(userPreferences),
    );
  }, [stateIsDefault, userPreferences, userId]);

  const currentSection = useMemo(() => {
    if (location.pathname.includes("/refine/logistics")) return 2;
    if (location.pathname.includes("/refine/fit")) return 1;
    return 0;
  }, [location.pathname]);

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
      <Breadcrumb
        className="mb-6"
        items={[
          { label: "Home", href: "/" },
          { label: "Discovery", href: "/discovery" },
          { label: "Refine matches", href: "/discovery/refine" },
        ]}
      />

      {/* 3-section progress */}
      <div className="flex items-center gap-x-2">
        {SECTIONS.map((section, i) => (
          <div
            key={section.label}
            className={cn(
              "h-2 flex-1 rounded transition-colors",
              i <= currentSection ? "bg-brand-400" : "bg-brand-100",
            )}
          />
        ))}
      </div>
      <p className="mt-3 text-center text-sm text-gray-500">
        {SECTIONS[currentSection].label} — Section {currentSection + 1} of{" "}
        {SECTIONS.length}
      </p>

      <Outlet />
    </div>
  );
}
